// src/pages/PlatformAdminConfiguracion.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, getDocs } from 'firebase/firestore';
import PlatformBackButton from '../components/PlatformBackButton';
// Importamos muchos iconos para esta vista
import {
    FaStar,
    FaShieldAlt,
    FaBell,
    FaUsersCog,
    FaKey,
    FaPaintBrush,
    FaPlug,
    FaCogs,
    FaPlus,
    FaPen,
    FaChartBar,
    FaUserShield,
    FaFileInvoiceDollar,
} from 'react-icons/fa';
import './PlatformAdminConfiguracion.css';
import ManagerConfiguracionAmenidades from './ManagerConfiguracionAmenidades';
import GestionarModulosModal from '../components/GestionarModulosModal';

const PlatformAdminConfiguracion = () => {
    const navigate = useNavigate();
    // Estado para las métricas, inicializado con los valores estáticos
    const [stats, setStats] = useState({
        configuraciones: 7,
        planesActivos: 0,
        zonasConfiguradas: 0,
        fraccionamientos: 0,
        fraccionamientosActivos: 0,
        residentes: 0,
        administradores: 0,
    });
    const [loading, setLoading] = useState(true);
    const [showAmenidades, setShowAmenidades] = useState(false);
    const [showModulosModal, setShowModulosModal] = useState(false);
    const [selectedFraccionamiento, setSelectedFraccionamiento] = useState(null); // eslint-disable-next-line no-unused-vars

    // useEffect para obtener las métricas dinámicas de fraccionamientos y usuarios
    useEffect(() => {
        const fetchStats = async () => {
            try {
                // Obtenemos los conteos dinámicos en paralelo
                const [planesSnapshot, fraccsSnapshot, usuariosSnapshot] = await Promise.all([
                    getDocs(collection(db, 'planes_suscripcion')),
                    getDocs(collection(db, 'fraccionamientos')),
                    getDocs(collection(db, 'usuarios')),
                ]);

                let activos = 0;
                let totalAmenidades = 0;

                fraccsSnapshot.forEach((docSnap) => {
                    const data = docSnap.data() || {};
                    const estado = typeof data.estado === 'string' ? data.estado.toLowerCase() : '';
                    const estatus = typeof data.estatus === 'string' ? data.estatus.toLowerCase() : '';
                    const activoFlag = data.activo === true || estado === 'activo' || estatus === 'activo';
                    if (activoFlag) {
                        activos += 1;
                    }

                    if (Array.isArray(data.amenidadesActivas)) {
                        totalAmenidades += data.amenidadesActivas.length;
                    }
                });

                // Contar usuarios reales por roles (administradores vs residentes/caseta)
                let residentCount = 0;
                let adminCount = 0;

                usuariosSnapshot.forEach((userDoc) => {
                    const userData = userDoc.data() || {};
                    const rol = (userData.rol || '').toLowerCase().trim();

                    // Roles de administración
                    if (['superadmin', 'platformadmin', 'admin', 'manager', 'gerente', 'administrador'].includes(rol)) {
                        adminCount += 1;
                    } 
                    // Roles de residentes (user) y caseta
                    else if (['residente', 'user', 'caseta', 'vigilante', 'guardia', 'inquilino', 'copropietario'].includes(rol) || !rol) {
                        residentCount += 1;
                    }
                });

                // Actualizamos el estado basado en el estado anterior para no perder los valores estáticos
                setStats(prevStats => ({
                    ...prevStats,
                    planesActivos: planesSnapshot.size,
                    fraccionamientos: fraccsSnapshot.size,
                    fraccionamientosActivos: activos,
                    residentes: residentCount,
                    administradores: adminCount,
                    zonasConfiguradas: totalAmenidades,
                }));

            } catch (error) {
                console.error("Error al obtener estadísticas:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchStats();
    }, []);

    const quickActions = [
        {
            key: 'nuevo',
            label: 'Agregar Nuevo',
            description: 'Crear roles y fraccionamientos',
            icon: FaPlus,
            to: '/agregar-nuevo',
        },
        {
            key: 'editar',
            label: 'Editar Información',
            description: 'Editar perfil de usuarios autenticados',
            icon: FaPen,
            to: '/usuarios-autenticados',
        },
        {
            key: 'accesos',
            label: 'Gestionar Accesos',
            description: 'Control de permisos y módulos',
            icon: FaUserShield,
            action: 'openModulosModal',
        },
        {
            key: 'facturacion',
            label: 'Facturación, Crea y edita notas de pago y presupuestos',
            description: 'Presupuestos formales, notas de pago y remisiones con folios automáticos y SendGrid',
            icon: FaFileInvoiceDollar,
            to: '/facturacion-presupuestos',
        },
    ];

    const handleQuickAction = (action, destination) => {
        if (action === 'openModulosModal') {
            setShowModulosModal(true);
            return;
        }
        if (!destination) {
            return;
        }
        navigate(destination);
    };

    const toggleAmenidades = () => {
        setShowAmenidades((prev) => !prev);
    };

    return (
        <div className="platform-config-container">
            {/* Tarjeta Principal Unificada: Cabecera + Gestión de Fraccionamientos */}
            <section className="platform-config-card management-section">
                <div className="card-top-header">
                    <div className="title-block">
                        <div className="header-actions-left-inline">
                            <PlatformBackButton />
                        </div>
                        <div className="section-icon-circle purple-glow">
                            <FaCogs />
                        </div>
                        <div>
                            <h2>Configuración y Gestión de Plataforma</h2>
                            <p>Administra parámetros globales, planes, amenidades y fraccionamientos registrados.</p>
                        </div>
                    </div>
                    <button type="button" className="btn-amenidades" onClick={toggleAmenidades}>
                        {showAmenidades ? 'Ocultar Amenidades' : 'Gestionar amenidades'}
                    </button>
                </div>

                {/* Stats Row (Oculto en pantallas pequeñas) */}
                <div className="stats-row-container">
                    <div className="stat-box">
                        <span className="stat-number text-blue">{loading ? '...' : stats.fraccionamientos}</span>
                        <small className="stat-label">FRACCIONAMIENTOS</small>
                    </div>
                    <div className="stat-box">
                        <span className="stat-number text-blue">{loading ? '...' : stats.fraccionamientosActivos}</span>
                        <small className="stat-label">ACTIVOS</small>
                    </div>
                    <div className="stat-box">
                        <span className="stat-number text-blue">{loading ? '...' : stats.residentes}</span>
                        <small className="stat-label">RESIDENTES</small>
                    </div>
                    <div className="stat-box">
                        <span className="stat-number text-blue">{loading ? '...' : stats.administradores}</span>
                        <small className="stat-label">ADMINISTRADORES</small>
                    </div>
                </div>

                {/* Quick Actions Row */}
                <div className="quick-actions-row">
                    {quickActions.map((action) => {
                        const Icon = action.icon;
                        return (
                            <button
                                key={action.key}
                                type="button"
                                className="action-button-card"
                                onClick={() => handleQuickAction(action.action, action.to)}
                            >
                                <div className="action-btn-icon">
                                    <Icon />
                                </div>
                                <div className="action-btn-text">
                                    <strong>{action.label}</strong>
                                    <small>{action.description}</small>
                                </div>
                            </button>
                        );
                    })}
                </div>
            </section>

            {showAmenidades && (
                <section className="platform-config-card amenidades-panel">
                    <div className="card-top-header">
                        <div>
                            <h3>Gestión de Amenidades</h3>
                            <p>Selecciona un fraccionamiento para activar o desactivar amenidades disponibles.</p>
                        </div>
                    </div>
                    <ManagerConfiguracionAmenidades showBackButton={false} />
                </section>
            )}

            {/* Grid de Configuraciones Generales (Tarjetas Inferiores) */}
            <div className="settings-grid">
                <Link to="/configuracion-planes" className="setting-card">
                    <div className="setting-icon text-blue"><FaStar /></div>
                    <h4>Planes y Precios</h4>
                    <p>Define los límites y costos de cada plan de suscripción.</p>
                </Link>

                <Link to="/configuracion/seguridad" className="setting-card">
                    <div className="setting-icon text-blue"><FaShieldAlt /></div>
                    <h4>Seguridad</h4>
                    <p>SSL forzado, backups automáticos y logs de auditoría.</p>
                </Link>

                <Link to="/configuracion/notificaciones" className="setting-card">
                    <div className="setting-icon text-blue"><FaBell /></div>
                    <h4>Notificaciones del Sistema</h4>
                    <p>Visualiza los logs de movimientos y actividad de cada sesión por fraccionamiento.</p>
                </Link>

                <Link to="/configuracion/sesiones" className="setting-card">
                    <div className="setting-icon text-blue"><FaKey /></div>
                    <h4>Control de Sesiones</h4>
                    <p>Máximo de intentos de login y sesiones simultáneas.</p>
                </Link>

                <Link to="/configuracion/branding" className="setting-card">
                    <div className="setting-icon text-blue"><FaPaintBrush /></div>
                    <h4>Branding y Apariencia</h4>
                    <p>Personaliza el logo y colores en las comunicaciones.</p>
                </Link>

                <Link to="/configuracion/integraciones" className="setting-card">
                    <div className="setting-icon text-blue"><FaPlug /></div>
                    <h4>Integraciones y Novedades</h4>
                    <p>Publica actualizaciones globales de la plataforma (changelogs) y gestiona integraciones.</p>
                </Link>
            </div>

            {/* Modal de Gestión de Módulos */}
            <GestionarModulosModal
                isOpen={showModulosModal}
                onClose={() => setShowModulosModal(false)}
                fraccionamientoId={selectedFraccionamiento}
            />
        </div>
    );
};

export default PlatformAdminConfiguracion;